import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    IconButton
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1516116216624-53e697fedbea";

/* ✅ Styled Components - Premium Dark Glass Theme */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/**
 * TypeScriptTypeGuards Component
 * * Explains runtime type narrowing using typeof, in, and custom predicates.
 * Optimized with TTS accessibility and clean code for copy-pasting.
 */
export default function TypeScriptTypeGuards() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* ✅ UI State Management */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);

    /* ✅ FAB Visibility Logic */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    /* ✅ Audio Controls */
    const handleSpeak = (text) => {
        window.speechSynthesis.cancel();
        if (isSpeaking) {
            setIsSpeaking(false);
            return;
        }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.95;
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
    };

    const stopSpeech = () => {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
    };

    /* ✅ Utility: Copy Code */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ✅ Educational Data */
    const guardModules = useMemo(() => [
        {
            title: "1. The 'typeof' Guard",
            tag: "Primitive Narrowing",
            desc: "Use 'typeof' to check for primitive types at runtime. Once inside the 'if' block, TypeScript knows the exact type and provides appropriate autocomplete (like .toUpperCase() for strings).",
            code: `function printId(id: number | string) {\n  if (typeof id === "string") {\n    // Here id is definitely a string\n    console.log(id.toUpperCase());\n  } else {\n    // Here id is definitely a number\n    console.log(id.toFixed(2));\n  }\n}`
        },
        {
            title: "2. The 'in' Guard",
            tag: "Object Property Check",
            desc: "The 'in' operator checks if a specific property exists on an object. This is perfect for distinguishing between two custom types within a Union based on their unique keys.",
            code: `type Dog = { bark: () => void };\ntype Cat = { meow: () => void };\n\nfunction makeSound(animal: Dog | Cat) {\n  if ("bark" in animal) {\n    animal.bark(); // TypeScript knows this is a Dog\n  } else {\n    animal.meow(); // TypeScript knows this is a Cat\n  }\n}`
        },
        {
            title: "3. Custom Type Predicates",
            tag: "User-Defined Guards",
            desc: "A type predicate is a special return type in the format 'parameterName is Type'. This allows you to create reusable logic that tells TypeScript how to narrow a variable's type.",
            code: `type Fish = { swim: () => void };\ntype Bird = { fly: () => void };\n\n// The 'pet is Fish' part is the predicate\nfunction isFish(pet: Fish | Bird): pet is Fish {\n  return (pet as Fish).swim !== undefined;\n}\n\nfunction move(pet: Fish | Bird) {\n  if (isFish(pet)) {\n    pet.swim();\n  } else {\n    pet.fly();\n  }\n}`
        }
    ], []);

    return (
        <PageWrapper>
            {/* 🏆 Header Section */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h4" : "h3"}
                    sx={{
                        fontWeight: 800,
                        backgroundColor: "goldenrod",
                        color: "#100720",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    TYPESCRIPT FUNDAMENTALS
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 🎯 Introduction Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h5" fontWeight={800} color="goldenrod">
                                Type Guards & Narrowing
                            </Typography>
                            <IconButton
                                onClick={() => handleSpeak("Type guards help you refine variable types at runtime, making your code more type-safe and intelligent.")}
                                sx={{ color: "goldenrod" }}
                            >
                                <VolumeUpIcon />
                            </IconButton>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.7 }}>
                            Narrowing is the process of TypeScript deducing a more specific type for a value based on the code's logic. By using <b>Type Guards</b>, you provide the compiler with "checkpoints" to ensure that the operations you perform on a variable are safe and valid for its current state.
                        </Typography>

                        <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
                            <Typography variant="caption" sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", p: 1, borderRadius: 1, color: "goldenrod" }}>
                                • Runtime Validation
                            </Typography>
                            <Typography variant="caption" sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", p: 1, borderRadius: 1, color: "goldenrod" }}>
                                • Logic-Based Narrowing
                            </Typography>
                            <Typography variant="caption" sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", p: 1, borderRadius: 1, color: "goldenrod" }}>
                                • custom is Type
                            </Typography>
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Content Rendering */}
                {guardModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiFillBulb size={24} />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>
                                            {item.title}
                                        </Typography>
                                        <Typography variant="caption" color="goldenrod" fontWeight={800}>
                                            {item.tag}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title="Listen">
                                        <IconButton onClick={() => handleSpeak(`${item.title}. ${item.desc}`)} sx={{ color: "goldenrod" }}>
                                            <VolumeUpIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{ color: "goldenrod", borderColor: "goldenrod" }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, lineHeight: 1.6 }}>
                                {item.desc}
                            </Typography>

                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Navigation Footer */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4, mb: 10 }}>
                    <Link to="/typescript-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            NEXT: UTILITY TYPES
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Control FABs */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                {isSpeaking && (
                    <Zoom in={isSpeaking}>
                        <Fab size="medium" color="error" onClick={stopSpeech}>
                            <StopIcon />
                        </Fab>
                    </Zoom>
                )}
                <Zoom in={showScroll}>
                    <Fab
                        size="small"
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        sx={{ bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                    >
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </PageWrapper>
    );
}
